/**
 * StaffPdfActions — staff-only quick actions for downloading the schema-driven
 * "internal staff working copy" PDF for a matter's applicant and sponsor.
 *
 * Same code path as IntakeFormV2's Download PDF button, but staff don't need
 * to navigate into the intake to fetch a copy.
 */

import { useEffect, useState } from 'react';
import { Download, Loader2, Mail, Check } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { SUPPLEMENTARY_FORM_CODES_SQL } from '../lib/formCodes';
import { pickFormId, hasSponsorForm, sponsorRoleNoun } from '../lib/schema-runtime';

// approved_pdf_templates not in database.types.ts yet (migration
// 20260510000001). Use an untyped facade for the existence check.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const sb: any = supabase;

interface StaffPdfActionsProps {
  matterId: string;
  matterCode: string | null;
  visaSubclass: string;
  variant: string | null;
  applicantContactId: string;
  sponsorContactId: string | null;
}

type Role = 'applicant' | 'sponsor';

// Form 888 only applies to partner / PMV visas.
const F888_VISAS = new Set(['820', '801', '309', '100', '300']);

export default function StaffPdfActions({
  matterId,
  matterCode,
  visaSubclass,
  variant,
  applicantContactId,
  sponsorContactId,
}: StaffPdfActionsProps) {
  const [busy, setBusy] = useState<Role | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [f888Busy, setF888Busy] = useState(false);
  const [f888Status, setF888Status] = useState<'idle' | 'sent' | 'error'>('idle');
  const [f888Error, setF888Error] = useState<string | null>(null);
  const [hasActiveF888Template, setHasActiveF888Template] = useState(false);

  // F888 button only renders when an admin has uploaded + activated the
  // official Home Affairs Form 888 PDF in /admin/approved-pdfs. The
  // edge function falls back to the legacy HTML render when no active
  // template exists, but the staff-facing button stays hidden until the
  // template is curated. The old VITE_FEATURE_F888 env var is no longer
  // read.
  useEffect(() => {
    let cancelled = false;
    if (!F888_VISAS.has(visaSubclass)) {
      setHasActiveF888Template(false);
      return;
    }
    void (async () => {
      const { data } = await sb
        .from('approved_pdf_templates')
        .select('id')
        .eq('form_code', 'F888')
        .eq('is_active', true)
        .maybeSingle();
      if (!cancelled) setHasActiveF888Template(!!data);
    })();
    return () => {
      cancelled = true;
    };
  }, [visaSubclass]);

  const showF888 = F888_VISAS.has(visaSubclass) && hasActiveF888Template;

  // Second-party form differs by subclass: 600-family = Form 1149, 600-tourist
  // = ADI's own Inviter form (no Department form number, so no number in the
  // label), partner visas = 40SP.
  const sponsorFormId = pickFormId(visaSubclass, true, variant);
  const sponsorFormLabel =
    sponsorFormId === '600_tourist_inviter'
      ? 'Inviter'
      : sponsorFormId === '1149_family_visitor_sponsor'
        ? 'Sponsor (1149)'
        : 'Sponsor (40SP)';

  const handleSendForm888 = async () => {
    setF888Busy(true);
    setF888Error(null);
    setF888Status('idle');
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;
      if (!accessToken) throw new Error('Sign-in expired. Refresh and try again.');
      const supabaseUrl = (import.meta.env.VITE_SUPABASE_URL as string) ?? '';
      if (!supabaseUrl) throw new Error('VITE_SUPABASE_URL is not configured.');

      const res = await fetch(`${supabaseUrl}/functions/v1/send-form-888-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({ matter_id: matterId, applicant_contact_id: applicantContactId }),
      });
      if (!res.ok) {
        const txt = await res.text().catch(() => '');
        // The edge function returns { error: "..." }. Surface that clear,
        // staff-facing message (e.g. the witness-email guard) rather than a
        // raw status dump.
        let message = txt;
        try {
          const parsed = JSON.parse(txt) as { error?: string };
          if (parsed?.error) message = parsed.error;
        } catch { /* not JSON, keep the raw text */ }
        throw new Error(message || `Form 888 email failed (${res.status}).`);
      }
      setF888Status('sent');
    } catch (err) {
      setF888Status('error');
      setF888Error(err instanceof Error ? err.message : 'Form 888 email failed.');
    } finally {
      setF888Busy(false);
    }
  };

  const handleDownload = async (role: Role) => {
    setBusy(role);
    setError(null);
    try {
      const contactId = role === 'sponsor' ? sponsorContactId : applicantContactId;
      // "inviter" on the 600 tourist stream, "sponsor" elsewhere.
      const roleNoun = role === 'sponsor' ? sponsorRoleNoun(visaSubclass) : role;
      if (!contactId) throw new Error(`No ${roleNoun} contact on this matter.`);

      const { data: rows, error: findErr } = await supabase
        .from('intake_forms')
        .select('id')
        .eq('matter_id', matterId)
        .eq('contact_id', contactId)
        .not('form_code', 'in', SUPPLEMENTARY_FORM_CODES_SQL)
        .order('last_saved_at', { ascending: false })
        .limit(1);
      if (findErr) throw findErr;
      const intakeFormId = rows?.[0]?.id;
      if (!intakeFormId) {
        throw new Error(
          `No intake form for the ${roleNoun} yet. They have not started the portal.`,
        );
      }

      const formId = pickFormId(visaSubclass, role === 'sponsor', variant);
      if (!formId) throw new Error(`No schema mapped for visa ${visaSubclass}/${variant}.`);

      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;
      if (!accessToken) throw new Error('Sign-in expired. Refresh and try again.');

      const pdfApiUrl = import.meta.env.VITE_PDF_API_URL ?? '/api/render-pdf';
      const response = await fetch(pdfApiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({ matter_id: matterId, form_id: formId, intake_form_id: intakeFormId }),
      });
      if (!response.ok) {
        const body = await response.text().catch(() => '');
        throw new Error(`PDF generation failed: ${response.status} ${body}`.trim());
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${formId}_${matterCode ?? matterId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'PDF download failed.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="card">
      <div className="section flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="text-sm font-semibold">Working-copy PDF</div>
          <div className="text-xs text-slate-500 mt-0.5">
            Internal staff document. Uses everything the client has filled so far. Empty fields show as “—”.
          </div>
          {error && <div className="text-xs text-rose-600 mt-2">{error}</div>}
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <button
            type="button"
            className="btn btn-sm"
            disabled={busy !== null}
            onClick={() => handleDownload('applicant')}
          >
            {busy === 'applicant' ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            <span>Applicant</span>
          </button>
          {sponsorContactId && hasSponsorForm(visaSubclass) && (
            <button
              type="button"
              className="btn btn-sm"
              disabled={busy !== null}
              onClick={() => handleDownload('sponsor')}
            >
              {busy === 'sponsor' ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              <span>{sponsorFormLabel}</span>
            </button>
          )}
        </div>
      </div>

      {showF888 && (
        <div className="section border-t border-slate-100 flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="text-sm font-semibold">Form 888 (witness statutory declaration)</div>
            <div className="text-xs text-slate-500 mt-0.5">
              Generates a prefilled Form 888 from the applicant's witness section and emails it to the applicant. They print, hand to each witness for signing, then upload the signed copies.
            </div>
            {f888Error && <div className="text-xs text-rose-600 mt-2">{f888Error}</div>}
            {f888Status === 'sent' && (
              <div className="text-xs text-emerald-700 mt-2 flex items-center gap-1">
                <Check className="w-3 h-3" /> Sent to the applicant.
              </div>
            )}
          </div>
          <div className="flex-shrink-0">
            <button
              type="button"
              className="btn btn-sm"
              disabled={f888Busy}
              onClick={handleSendForm888}
            >
              {f888Busy ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Mail className="w-4 h-4" />
              )}
              <span>Send Form 888 to applicant</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
